import { TFunction } from "i18next";

import { roundToCents } from "../utils/roundToCents";
import { ResultText, SpousalMaintenanceCalculation, SpousalMaintenanceInput } from "./SpousalMaintenance.types";

// Calculates the spousal-maintenance claim ("Gattenunterhalt", § 94 Abs. 3 ABGB) shown by the
// calculator. Direct port of the legacy Ruby reference (the ground truth), previously computed on the
// backend:
// - Client has NO own income (0) → 33%-rule: (33 − 4·children)% of the spouse's free income
//   (spouse income minus housing costs when living together).
// - Client HAS own income → 40%-rule with Günstigkeitsvergleich: (40 − 4·children)% of the joint free
//   income minus own income, capped at the 33%-rule amount so having an income never yields more.
// The percentage is floored at 0 (many children) and the final claim is clamped to ≥ 0. Pure function.

// Legal rule coefficients (§ 94 Abs. 3 ABGB) — also used by the summary builder below.
const NO_OWN_INCOME_PERCENT = 33;
const WITH_OWN_INCOME_PERCENT = 40;
const PER_CHILD_REDUCTION_PERCENT = 4;
const PERCENT_DIVISOR = 100;

// The base percentage reduced by 4 points per child, floored at 0.
function percentAfterChildren(basePercent: number, children: number): number {
  return Math.max(basePercent - PER_CHILD_REDUCTION_PERCENT * children, 0);
}

export function calculateSpousalMaintenance(input: SpousalMaintenanceInput): SpousalMaintenanceCalculation {
  // Defensive normalisation — the form only submits valid input, but the calculation must not trust it.
  const spouseIncome = Math.max(input.spouseIncome, 0);
  const ownIncome = Math.max(input.ownIncome, 0);
  const housingDeducted = input.livingTogether ? Math.max(input.housingCosts, 0) : 0;
  const children = Math.max(input.children, 0);

  // No own income (0) selects the 33%-rule; any positive own income selects the 40%-rule.
  const hasOwnIncome = ownIncome > 0;

  // Housing is deducted from the spouse's income only, and only when living together.
  const spouseFreeIncome = Math.max(spouseIncome - housingDeducted, 0);
  const jointIncome = spouseIncome + ownIncome; // gemeinsames Einkommen (no housing)
  const jointFreeIncome = spouseFreeIncome + ownIncome; // frei verfügbares Haushaltseinkommen

  let claim: number;
  let effectivePercent: number;
  let fortyRuleAmount: number;
  let thirdRuleAmount: number;
  let cappedAtThirdRule: boolean;
  let ownIncomeExceedsForty: boolean;

  if (!hasOwnIncome) {
    // 33%-rule.
    effectivePercent = percentAfterChildren(NO_OWN_INCOME_PERCENT, children);
    thirdRuleAmount = (spouseFreeIncome * effectivePercent) / PERCENT_DIVISOR;
    fortyRuleAmount = 0;
    claim = thirdRuleAmount;
    cappedAtThirdRule = false;
    ownIncomeExceedsForty = false;
  } else {
    // 40%-rule with Günstigkeitsvergleich against the 33%-rule.
    const percent40 = percentAfterChildren(WITH_OWN_INCOME_PERCENT, children);
    const percent33 = percentAfterChildren(NO_OWN_INCOME_PERCENT, children);
    effectivePercent = percent40;

    fortyRuleAmount = (jointFreeIncome * percent40) / PERCENT_DIVISOR;
    thirdRuleAmount = (spouseFreeIncome * percent33) / PERCENT_DIVISOR;

    const fortyRuleClaim = fortyRuleAmount - ownIncome;
    cappedAtThirdRule = fortyRuleClaim > thirdRuleAmount;
    claim = cappedAtThirdRule ? thirdRuleAmount : fortyRuleClaim;

    // Matches the legacy early-exit condition (own income > 40% of joint income, without the housing
    // deduction). When true, the 40%-rule claim is guaranteed ≤ 0.
    ownIncomeExceedsForty = ownIncome > (jointIncome * WITH_OWN_INCOME_PERCENT) / PERCENT_DIVISOR;
  }

  if (claim < 0) {
    claim = 0;
  }

  claim = roundToCents(claim);

  return {
    claim,
    effectivePercent,
    spouseFreeIncome,
    housingDeducted,
    jointIncome,
    jointFreeIncome,
    fortyRuleAmount,
    thirdRuleAmount,
    cappedAtThirdRule,
    ownIncomeExceedsForty,
  };
}

// de-DE (not de-AT) is used deliberately: it yields the dot-thousands / comma-decimal grouping the
// legacy tool and the requirement use ("1.200,00"). de-AT's current CLDR groups with a non-breaking
// space ("1 200,00"), which would not match. The "€" is appended manually so the symbol always trails.
//
// Deliberately NOT the shared formatEuro from ../format/formatNumber: that one formats through
// react-number-format, which *truncates* at the decimal scale, while the summary prints unrounded
// intermediates (fortyRuleAmount, jointFreeIncome, thirdRuleAmount). 40% of 987,65 € is 395,0592 €
// — "395,06 €" here, "395,05 €" through the shared helper. Intl's half-up rounding is what the
// legacy Ruby reference produces, so the two are not interchangeable in this file.
const amountFormatter = new Intl.NumberFormat("de-DE", {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

// Formats a number as a Euro amount with the symbol last, e.g. 396 → "396,00 €".
function formatEuro(amount: number): string {
  return `${amountFormatter.format(amount)} €`;
}

// Whole-number percentage (the rule percentages are always integers), e.g. 33 → "33".
function formatPercent(percent: number): string {
  return percent.toString();
}

// "(= 33% − 8% (= 2 Kinder × 4%))" — childCount is pluralized in the i18n resource.
function childReductionParenthetical(t: TFunction, basePercent: number, children: number): string {
  const reduction = children * PER_CHILD_REDUCTION_PERCENT;
  const childText = t("spousalMaintenance.summary.childCount", { count: children });
  return t("spousalMaintenance.summary.parenthetical", { basePercent, reduction, childText });
}

function noOwnIncomeSummary(t: TFunction, input: SpousalMaintenanceInput, calc: SpousalMaintenanceCalculation): string {
  const incomeBlock = input.livingTogether
    ? t("spousalMaintenance.summary.noOwnIncome.incomeTogether", {
        spouseIncome: formatEuro(input.spouseIncome),
        housingDeducted: formatEuro(calc.housingDeducted),
        spouseFreeIncome: formatEuro(calc.spouseFreeIncome),
      })
    : t("spousalMaintenance.summary.noOwnIncome.incomeApart", { spouseIncome: formatEuro(input.spouseIncome) });

  const legalBlock = t("spousalMaintenance.summary.noOwnIncome.legal", {
    effectivePercent: formatPercent(calc.effectivePercent),
    parenthetical: childReductionParenthetical(t, NO_OWN_INCOME_PERCENT, input.children),
    claim: formatEuro(calc.claim),
    spouseFreeIncome: formatEuro(calc.spouseFreeIncome),
  });

  return `${t("spousalMaintenance.summary.noOwnIncome.intro")}\n${incomeBlock}\n${legalBlock}`;
}

function ownIncomeSummary(t: TFunction, input: SpousalMaintenanceInput, calc: SpousalMaintenanceCalculation): string {
  const incomeBlock = input.livingTogether
    ? t("spousalMaintenance.summary.ownIncome.incomeTogether", {
        jointIncome: formatEuro(calc.jointIncome),
        housingDeducted: formatEuro(calc.housingDeducted),
        jointFreeIncome: formatEuro(calc.jointFreeIncome),
      })
    : t("spousalMaintenance.summary.ownIncome.incomeApart", { jointIncome: formatEuro(calc.jointIncome) });

  const legalBlock = t("spousalMaintenance.summary.ownIncome.legal", {
    effectivePercent: formatPercent(calc.effectivePercent),
    parenthetical: childReductionParenthetical(t, WITH_OWN_INCOME_PERCENT, input.children),
    fortyRuleAmount: formatEuro(calc.fortyRuleAmount),
    ownIncome: formatEuro(input.ownIncome),
  });

  const capBlock = calc.cappedAtThirdRule
    ? t("spousalMaintenance.summary.ownIncome.cap", { thirdRuleAmount: formatEuro(calc.thirdRuleAmount) })
    : "";

  const intro = t("spousalMaintenance.summary.ownIncome.intro", {
    ownIncome: formatEuro(input.ownIncome),
    spouseIncome: formatEuro(input.spouseIncome),
  });

  return `${intro}\n${incomeBlock}\n${legalBlock}${capBlock}`;
}

// Builds the red result headline + the first-person legal summary from the calculation result.
export function buildResultText(
  t: TFunction,
  input: SpousalMaintenanceInput,
  calc: SpousalMaintenanceCalculation
): ResultText {
  if (calc.ownIncomeExceedsForty) {
    return { headline: t("spousalMaintenance.ownIncomeExceedsForty"), summary: null };
  }

  const headline = t("spousalMaintenance.claimHeadline", { claim: formatEuro(calc.claim) });
  const summary = input.ownIncome > 0 ? ownIncomeSummary(t, input, calc) : noOwnIncomeSummary(t, input, calc);

  return { headline, summary };
}
